// HOMESCHOOL-03 (deep): fine-grained syllabus outcomes catalog.
//
// This is the *content* layer that turns loose curriculum tags into
// specific, teacher-facing learning objectives that map to app activities
// and Term Report evidence lines.
//
// Structure:
//   SYLLABUS[curriculumId][subjectId] = [{ code, outcome, ageBand }]
//
// Coverage priorities (in order): UK EYFS/KS1-KS3, IB PYP/MYP, Cambridge
// Primary/Lower-Sec, UAE MOE (KG-Cycle 2), Common Core K-8. Every subject
// (Maths / English / Science / History / Geography / Art / Music / RE /
// PE / Computing / Languages / PSHE) has age-banded outcomes. Keep the
// objective count tight but authoritative — every outcome is one activity
// can practise, and the Term Report cites them verbatim.

export const SYLLABUS = {
  // ═══════════════════════════════════════════════════════════════════
  //   UK — EYFS (3-5)
  // ═══════════════════════════════════════════════════════════════════
  eyfs: {
    literacy: [
      { code: "eyfs-lit-1", outcome: "Comprehension: engage with stories, non-fiction, rhymes and poems.", ageBand: [3,5] },
      { code: "eyfs-lit-2", outcome: "Word Reading: read simple phrases and sentences using phonic knowledge.", ageBand: [4,5] },
      { code: "eyfs-lit-3", outcome: "Writing: form lower-case and capital letters correctly.", ageBand: [4,5] },
    ],
    maths: [
      { code: "eyfs-ma-1",  outcome: "Number: subitise up to 5; know number bonds to 5.", ageBand: [3,5] },
      { code: "eyfs-ma-2",  outcome: "Numerical Patterns: count beyond 10; compare quantities.", ageBand: [4,5] },
    ],
    world: [
      { code: "eyfs-utw-1", outcome: "People, Culture & Communities: describe own community and beyond.", ageBand: [3,5] },
      { code: "eyfs-utw-2", outcome: "The Natural World: explore plants, animals and seasons.", ageBand: [3,5] },
      { code: "eyfs-utw-3", outcome: "Past & Present: talk about the lives of people around them.", ageBand: [4,5] },
    ],
    expressive: [
      { code: "eyfs-ea-1",  outcome: "Creating with Materials: use paint, drawing, printing and sculpture.", ageBand: [3,5] },
      { code: "eyfs-ea-2",  outcome: "Being Imaginative: perform songs, rhymes, poems and stories.", ageBand: [3,5] },
    ],
    physical: [
      { code: "eyfs-pd-1",  outcome: "Gross Motor: move confidently through balance, control and coordination.", ageBand: [3,5] },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  //   UK KS1 (5-7)
  // ═══════════════════════════════════════════════════════════════════
  "uk-ks1": {
    english: [
      { code: "uk-ks1-en-1", outcome: "Reading: apply phonic knowledge to decode words fluently.",           ageBand: [5,7] },
      { code: "uk-ks1-en-2", outcome: "Reading: comprehend simple texts and answer questions about them.",  ageBand: [5,7] },
      { code: "uk-ks1-en-3", outcome: "Writing: compose sentences with correct spelling and punctuation.",  ageBand: [6,7] },
      { code: "uk-ks1-en-4", outcome: "Spoken language: listen and respond in discussions.",                 ageBand: [5,7] },
    ],
    maths: [
      { code: "uk-ks1-ma-1", outcome: "Number: count, read and write to 100; count in 2s, 5s and 10s.",     ageBand: [5,6] },
      { code: "uk-ks1-ma-2", outcome: "Addition & Subtraction: recall facts to 20 fluently.",                ageBand: [5,7] },
      { code: "uk-ks1-ma-3", outcome: "Multiplication & Division: 2, 5, 10 times tables.",                   ageBand: [6,7] },
      { code: "uk-ks1-ma-4", outcome: "Fractions: recognise 1/2 and 1/4 of a shape or quantity.",            ageBand: [6,7] },
      { code: "uk-ks1-ma-5", outcome: "Measurement: use rulers, scales, thermometers; tell time to the hour.", ageBand: [6,7] },
      { code: "uk-ks1-ma-6", outcome: "Money: recognise and value coins and notes.",                          ageBand: [6,7] },
      { code: "uk-ks1-ma-7", outcome: "Geometry: name 2-D and 3-D shapes and their properties.",              ageBand: [5,7] },
    ],
    science: [
      { code: "uk-ks1-sc-1", outcome: "Living Things: identify and classify common animals.",                ageBand: [5,6] },
      { code: "uk-ks1-sc-2", outcome: "Plants: identify parts of plants and how they grow.",                 ageBand: [5,7] },
      { code: "uk-ks1-sc-3", outcome: "Seasonal Change: observe changes across the four seasons.",           ageBand: [5,7] },
      { code: "uk-ks1-sc-4", outcome: "Materials: compare and group everyday materials.",                    ageBand: [6,7] },
      { code: "uk-ks1-sc-5", outcome: "Habitats: describe how habitats provide food and shelter.",           ageBand: [6,7] },
    ],
    geography: [
      { code: "uk-ks1-geo-1", outcome: "Locate the world's 7 continents and 5 oceans.",                     ageBand: [5,7] },
      { code: "uk-ks1-geo-2", outcome: "Name and locate the 4 UK countries and capitals.",                  ageBand: [5,7] },
      { code: "uk-ks1-geo-3", outcome: "Use maps, atlases and globes to identify countries studied.",       ageBand: [5,7] },
      { code: "uk-ks1-geo-4", outcome: "Compare a small area of UK with a contrasting non-European country.", ageBand: [6,7] },
    ],
    history: [
      { code: "uk-ks1-hi-1", outcome: "Changes within living memory (e.g. toys, transport, technology).",   ageBand: [5,7] },
      { code: "uk-ks1-hi-2", outcome: "Significant historical events beyond living memory.",                ageBand: [6,7] },
      { code: "uk-ks1-hi-3", outcome: "Lives of significant people (explorers, monarchs, scientists).",     ageBand: [6,7] },
    ],
    art: [
      { code: "uk-ks1-art-1", outcome: "Use drawing, painting, printing and sculpture.",                    ageBand: [5,7] },
      { code: "uk-ks1-art-2", outcome: "Study the work of a range of artists, craftmakers and designers.",  ageBand: [5,7] },
    ],
    music: [
      { code: "uk-ks1-mu-1", outcome: "Sing songs and use voices creatively.",                              ageBand: [5,7] },
      { code: "uk-ks1-mu-2", outcome: "Listen with concentration and understanding to music.",              ageBand: [5,7] },
    ],
    computing: [
      { code: "uk-ks1-cp-1", outcome: "Algorithms: understand what algorithms are; create simple programs.", ageBand: [5,7] },
      { code: "uk-ks1-cp-2", outcome: "E-Safety: use technology safely and respectfully.",                  ageBand: [5,7] },
    ],
    pe: [
      { code: "uk-ks1-pe-1", outcome: "Master basic movements (running, jumping, throwing, catching).",     ageBand: [5,7] },
    ],
    re: [
      { code: "uk-ks1-re-1", outcome: "Learn about beliefs, festivals and celebrations of world religions.", ageBand: [5,7] },
    ],
    pshe: [
      { code: "uk-ks1-ps-1", outcome: "Health & Wellbeing: healthy eating, exercise, feelings.",            ageBand: [5,7] },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  //   UK KS2 (7-11)
  // ═══════════════════════════════════════════════════════════════════
  "uk-ks2": {
    english: [
      { code: "uk-ks2-en-1", outcome: "Reading: read wide range of fiction, poetry, plays and non-fiction.", ageBand: [7,11] },
      { code: "uk-ks2-en-2", outcome: "Retrieve, record and present information from non-fiction.",         ageBand: [8,11] },
      { code: "uk-ks2-en-3", outcome: "Writing: plan, draft, edit and evaluate own writing.",               ageBand: [8,11] },
      { code: "uk-ks2-en-4", outcome: "Grammar & Punctuation: use fronted adverbials, commas, apostrophes.", ageBand: [8,11] },
      { code: "uk-ks2-en-5", outcome: "Spelling: apply spelling patterns and rules; use dictionary.",       ageBand: [7,11] },
      { code: "uk-ks2-en-6", outcome: "Spoken language: give well-structured descriptions and explanations.", ageBand: [7,11] },
    ],
    maths: [
      { code: "uk-ks2-ma-1", outcome: "Number: read, write, order and compare numbers up to 10,000,000.",    ageBand: [9,11] },
      { code: "uk-ks2-ma-2", outcome: "Multiplication tables: recall all facts up to 12 × 12.",              ageBand: [8,10] },
      { code: "uk-ks2-ma-3", outcome: "Written methods: long multiplication and long division.",             ageBand: [9,11] },
      { code: "uk-ks2-ma-4", outcome: "Fractions: add, subtract, multiply, divide; equivalence and simplification.", ageBand: [8,11] },
      { code: "uk-ks2-ma-5", outcome: "Decimals & Percentages: convert between fractions, decimals, %.",     ageBand: [9,11] },
      { code: "uk-ks2-ma-6", outcome: "Ratio & Proportion: solve problems involving relative sizes.",        ageBand: [10,11] },
      { code: "uk-ks2-ma-7", outcome: "Algebra: express missing-number problems algebraically.",             ageBand: [10,11] },
      { code: "uk-ks2-ma-8", outcome: "Measurement: convert between units; calculate area and perimeter.",   ageBand: [7,11] },
      { code: "uk-ks2-ma-9", outcome: "Geometry: properties of 2-D & 3-D shapes; angles; coordinates.",      ageBand: [8,11] },
      { code: "uk-ks2-ma-10", outcome: "Statistics: interpret and construct tables, charts, line graphs.",   ageBand: [8,11] },
    ],
    science: [
      { code: "uk-ks2-sc-1", outcome: "Earth & Space: describe movement of Earth, Moon, planets.",           ageBand: [9,11] },
      { code: "uk-ks2-sc-2", outcome: "Living Things: life cycles of plants and animals.",                   ageBand: [7,9]  },
      { code: "uk-ks2-sc-3", outcome: "Classification: classify living things into broad groups.",           ageBand: [8,10] },
      { code: "uk-ks2-sc-4", outcome: "Light: light travels in straight lines; explain shadows.",            ageBand: [9,11] },
      { code: "uk-ks2-sc-5", outcome: "Forces: gravity, air resistance, water resistance and friction.",    ageBand: [9,11] },
      { code: "uk-ks2-sc-6", outcome: "Electricity: build simple series circuits.",                          ageBand: [8,10] },
      { code: "uk-ks2-sc-7", outcome: "Sound: how sounds are made and how they travel.",                     ageBand: [8,10] },
      { code: "uk-ks2-sc-8", outcome: "Rocks & Soils: compare rock types and how soils are formed.",         ageBand: [7,9]  },
      { code: "uk-ks2-sc-9", outcome: "States of Matter: solids, liquids and gases; change of state.",       ageBand: [8,10] },
      { code: "uk-ks2-sc-10", outcome: "Human Body: circulatory, digestive and muscular systems.",           ageBand: [10,11] },
    ],
    geography: [
      { code: "uk-ks2-geo-1", outcome: "Locate world's countries focusing on Europe and the Americas.",     ageBand: [7,11] },
      { code: "uk-ks2-geo-2", outcome: "UK: counties, cities and regions.",                                 ageBand: [7,11] },
      { code: "uk-ks2-geo-3", outcome: "Latitude, longitude, Equator, Hemispheres, Tropics, Poles.",         ageBand: [8,11] },
      { code: "uk-ks2-geo-4", outcome: "Compare a UK region with a European and a North/South American region.", ageBand: [8,11] },
      { code: "uk-ks2-geo-5", outcome: "Physical: climate zones, biomes, rivers, mountains, volcanoes.",     ageBand: [9,11] },
      { code: "uk-ks2-geo-6", outcome: "Human: land use, trade, natural resources, water cycle.",            ageBand: [9,11] },
    ],
    history: [
      { code: "uk-ks2-hi-1", outcome: "Stone Age to Iron Age Britain.",                                     ageBand: [7,9]  },
      { code: "uk-ks2-hi-2", outcome: "Roman Empire and its impact on Britain.",                            ageBand: [8,10] },
      { code: "uk-ks2-hi-3", outcome: "Anglo-Saxons and Vikings — settlement of Britain.",                  ageBand: [8,10] },
      { code: "uk-ks2-hi-4", outcome: "Earliest civilisations (Sumer, Indus, Egypt, Shang).",               ageBand: [8,11] },
      { code: "uk-ks2-hi-5", outcome: "Ancient Greece — life, achievements and influence.",                 ageBand: [9,11] },
      { code: "uk-ks2-hi-6", outcome: "Non-European society (Mayans, Benin, Baghdad c. AD 900).",           ageBand: [9,11] },
      { code: "uk-ks2-hi-7", outcome: "British history study beyond 1066 (Tudors / Victorians etc).",       ageBand: [9,11] },
    ],
    art: [
      { code: "uk-ks2-art-1", outcome: "Improve drawing, painting and sculpture mastery.",                  ageBand: [7,11] },
      { code: "uk-ks2-art-2", outcome: "Study artists, architects and designers in history.",               ageBand: [7,11] },
    ],
    music: [
      { code: "uk-ks2-mu-1", outcome: "Play and perform in solo and ensemble contexts.",                    ageBand: [7,11] },
      { code: "uk-ks2-mu-2", outcome: "Notation: use and understand staff and other musical notations.",    ageBand: [8,11] },
    ],
    computing: [
      { code: "uk-ks2-cp-1", outcome: "Design, write and debug programs; use sequence, selection, repetition.", ageBand: [7,11] },
      { code: "uk-ks2-cp-2", outcome: "Use logical reasoning to explain how simple algorithms work.",       ageBand: [7,11] },
      { code: "uk-ks2-cp-3", outcome: "Understand computer networks including the internet.",              ageBand: [9,11] },
      { code: "uk-ks2-cp-4", outcome: "E-Safety: identify a range of ways to report concerns.",             ageBand: [7,11] },
    ],
    pe: [
      { code: "uk-ks2-pe-1", outcome: "Play competitive games; apply basic principles of attacking/defending.", ageBand: [7,11] },
      { code: "uk-ks2-pe-2", outcome: "Swim competently, confidently over 25m; safe self-rescue.",           ageBand: [8,11] },
    ],
    languages: [
      { code: "uk-ks2-la-1", outcome: "Listen attentively to spoken language and engage in conversations.", ageBand: [7,11] },
      { code: "uk-ks2-la-2", outcome: "Read carefully and show understanding of words and phrases.",        ageBand: [8,11] },
      { code: "uk-ks2-la-3", outcome: "Write phrases from memory and adapt to create new sentences.",       ageBand: [9,11] },
    ],
    re: [
      { code: "uk-ks2-re-1", outcome: "Study beliefs, teachings and practices of major world religions.",   ageBand: [7,11] },
    ],
    pshe: [
      { code: "uk-ks2-ps-1", outcome: "Health & Wellbeing: mental health, healthy relationships, drug ed.", ageBand: [7,11] },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  //   UK KS3 (11-14)
  // ═══════════════════════════════════════════════════════════════════
  "uk-ks3": {
    english: [
      { code: "uk-ks3-en-1", outcome: "Read a wide range of high-quality, challenging classic literature.", ageBand: [11,14] },
      { code: "uk-ks3-en-2", outcome: "Writing: produce clear and coherent text for a range of purposes.",   ageBand: [11,14] },
      { code: "uk-ks3-en-3", outcome: "Grammar & Vocabulary: use Standard English with technical accuracy.", ageBand: [11,14] },
    ],
    maths: [
      { code: "uk-ks3-ma-1", outcome: "Algebra: expressions, formulae, equations, sequences.",              ageBand: [11,14] },
      { code: "uk-ks3-ma-2", outcome: "Ratio, proportion and rates of change.",                              ageBand: [11,14] },
      { code: "uk-ks3-ma-3", outcome: "Geometry & Measures: angles, area, volume, transformations.",         ageBand: [11,14] },
      { code: "uk-ks3-ma-4", outcome: "Probability and Statistics: describe and analyse data.",              ageBand: [11,14] },
    ],
    science: [
      { code: "uk-ks3-sc-1", outcome: "Biology: cells, organisms, ecosystems, genetics, evolution.",         ageBand: [11,14] },
      { code: "uk-ks3-sc-2", outcome: "Chemistry: atoms, elements, compounds, reactions, Earth's resources.", ageBand: [11,14] },
      { code: "uk-ks3-sc-3", outcome: "Physics: forces, energy, waves, electricity, magnetism, space.",      ageBand: [11,14] },
      { code: "uk-ks3-sc-4", outcome: "Working Scientifically: hypothesise, test, evaluate.",                ageBand: [11,14] },
    ],
    geography: [
      { code: "uk-ks3-geo-1", outcome: "Extend locational knowledge to Africa, Russia, Asia, Middle East.",  ageBand: [11,14] },
      { code: "uk-ks3-geo-2", outcome: "Physical: geological timescales, plate tectonics, weathering.",      ageBand: [11,14] },
      { code: "uk-ks3-geo-3", outcome: "Human: population, urbanisation, economic activity, globalisation.", ageBand: [12,14] },
      { code: "uk-ks3-geo-4", outcome: "Interpret Ordnance Survey maps at a range of scales.",              ageBand: [11,14] },
      { code: "uk-ks3-geo-5", outcome: "Weather & climate: global systems, climate change, sustainability.",  ageBand: [11,14] },
    ],
    history: [
      { code: "uk-ks3-hi-1", outcome: "Development of Church, state and society in Britain 1066–1509.",      ageBand: [11,13] },
      { code: "uk-ks3-hi-2", outcome: "Britain 1509–1745 (Reformation, Civil War, Restoration).",           ageBand: [12,14] },
      { code: "uk-ks3-hi-3", outcome: "Britain 1745–1901 (Industrial Revolution, Empire).",                  ageBand: [12,14] },
      { code: "uk-ks3-hi-4", outcome: "Challenges for Britain, Europe and the wider world 1901–present.",    ageBand: [13,14] },
    ],
    art: [
      { code: "uk-ks3-art-1", outcome: "Increase proficiency in drawing, painting and sculpture; sketchbooks.", ageBand: [11,15] },
      { code: "uk-ks3-art-2", outcome: "Analyse and critique great artists and design movements in history.", ageBand: [12,15] },
    ],
    music: [
      { code: "uk-ks3-mu-1", outcome: "Play, sing and improvise music fluently with increasing confidence.",  ageBand: [11,14] },
      { code: "uk-ks3-mu-2", outcome: "Compose and extend musical ideas, using staff and other notations.",   ageBand: [12,14] },
    ],
    re: [
      { code: "uk-ks3-re-1", outcome: "Study major world religions in depth — beliefs, ethics, contemporary issues.", ageBand: [11,14] },
    ],
    pshe: [
      { code: "uk-ks3-ps-1", outcome: "Health & Wellbeing: mental health, relationships, online safety, drugs.", ageBand: [11,14] },
    ],
    computing: [
      { code: "uk-ks3-cp-1", outcome: "Design and write algorithms using two or more programming languages.", ageBand: [11,14] },
      { code: "uk-ks3-cp-2", outcome: "Understand hardware and software components and how they interact.",  ageBand: [11,14] },
      { code: "uk-ks3-cp-3", outcome: "Boolean logic, binary numbers, data representation.",                 ageBand: [12,14] },
    ],
    languages: [
      { code: "uk-ks3-la-1", outcome: "Communicate in speech and writing with increasing fluency.",         ageBand: [11,14] },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  //   IB PYP (5-12)
  // ═══════════════════════════════════════════════════════════════════
  "ib-pyp": {
    language: [
      { code: "ib-pyp-lang-1", outcome: "Oral language: listen and speak with confidence in home & new languages.", ageBand: [5,12] },
      { code: "ib-pyp-lang-2", outcome: "Written language: read and view a variety of texts.",              ageBand: [5,12] },
      { code: "ib-pyp-lang-3", outcome: "Visual language: use and interpret visual texts.",                 ageBand: [5,12] },
    ],
    maths: [
      { code: "ib-pyp-ma-1", outcome: "Number: place value, four operations, fractions and decimals.",       ageBand: [5,12] },
      { code: "ib-pyp-ma-2", outcome: "Shape & Space: 2-D/3-D shapes, symmetry, transformations, position.", ageBand: [6,12] },
      { code: "ib-pyp-ma-3", outcome: "Data Handling: collect, organise, represent and interpret data.",     ageBand: [6,12] },
      { code: "ib-pyp-ma-4", outcome: "Measurement: length, mass, capacity, time, temperature, money.",      ageBand: [5,12] },
      { code: "ib-pyp-ma-5", outcome: "Pattern & Function: identify, describe and extend patterns.",         ageBand: [6,12] },
    ],
    science: [
      { code: "ib-pyp-sc-1", outcome: "Living Things: habitats, life cycles, classification.",              ageBand: [5,12] },
      { code: "ib-pyp-sc-2", outcome: "Earth & Space: solar system, weather, seasons, water cycle.",         ageBand: [6,12] },
      { code: "ib-pyp-sc-3", outcome: "Forces & Energy: light, sound, magnets, simple machines.",            ageBand: [7,12] },
      { code: "ib-pyp-sc-4", outcome: "Materials & Matter: states of matter, changes, properties.",          ageBand: [7,12] },
    ],
    social: [
      { code: "ib-pyp-so-1", outcome: "Human Systems: how organisations and communities function.",         ageBand: [7,12] },
      { code: "ib-pyp-so-2", outcome: "Continuity & Change: past-present-future thinking.",                  ageBand: [7,12] },
      { code: "ib-pyp-so-3", outcome: "Global Awareness: culture, environment, resources.",                  ageBand: [7,12] },
    ],
    geography: [
      { code: "ib-pyp-geo-1", outcome: "Where we are in place and time — locate places and communities.",   ageBand: [5,10] },
      { code: "ib-pyp-geo-2", outcome: "How the world works — natural systems and human impact.",           ageBand: [7,12] },
      { code: "ib-pyp-geo-3", outcome: "Sharing the planet — global citizenship, resources, rights.",       ageBand: [8,12] },
    ],
    arts: [
      { code: "ib-pyp-ar-1", outcome: "Dance, drama, music and visual arts as ways of knowing.",             ageBand: [5,12] },
    ],
    pshe: [
      { code: "ib-pyp-ps-1", outcome: "Personal, Social & Physical Education: healthy lifestyle choices.",   ageBand: [5,12] },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  //   IB MYP (11-15)
  // ═══════════════════════════════════════════════════════════════════
  "ib-myp": {
    language: [
      { code: "ib-myp-lang-1", outcome: "Language & Literature: analyse and interpret texts.",              ageBand: [11,15] },
      { code: "ib-myp-lang-2", outcome: "Language Acquisition: communicate in an additional language.",     ageBand: [11,15] },
    ],
    maths: [
      { code: "ib-myp-ma-1", outcome: "Number, algebra, geometry, statistics with real-world modelling.",    ageBand: [11,15] },
    ],
    science: [
      { code: "ib-myp-sc-1", outcome: "Integrated Science: inquiry, experimentation, communication.",        ageBand: [11,15] },
    ],
    society: [
      { code: "ib-myp-so-1", outcome: "Individuals & Societies: global systems, place, environment.",       ageBand: [11,15] },
    ],
    arts: [
      { code: "ib-myp-ar-1", outcome: "Arts: create, present, respond to art forms.",                       ageBand: [11,15] },
    ],
    design: [
      { code: "ib-myp-de-1", outcome: "Design cycle: inquire, develop, create and evaluate solutions.",     ageBand: [11,15] },
    ],
    pe: [
      { code: "ib-myp-pe-1", outcome: "PE & Health: knowledge and understanding of health-related fitness.",  ageBand: [11,15] },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  //   Cambridge Primary (5-11) — Stages 1-6
  // ═══════════════════════════════════════════════════════════════════
  "cam-prim": {
    english: [
      { code: "cam-prim-en-1", outcome: "Reading: comprehend, respond to and analyse a wide range of texts.", ageBand: [5,11] },
      { code: "cam-prim-en-2", outcome: "Writing: use appropriate structures and vocabulary.",                ageBand: [5,11] },
      { code: "cam-prim-en-3", outcome: "Speaking & Listening: express ideas clearly.",                       ageBand: [5,11] },
    ],
    maths: [
      { code: "cam-prim-ma-1", outcome: "Number: understand number, calculation, and fractions.",             ageBand: [5,11] },
      { code: "cam-prim-ma-2", outcome: "Geometry & Measure: shape, position, movement, units.",              ageBand: [5,11] },
      { code: "cam-prim-ma-3", outcome: "Statistics: read and draw charts, tables, pictograms.",              ageBand: [6,11] },
      { code: "cam-prim-ma-4", outcome: "Problem Solving: apply concepts to real-life contexts.",             ageBand: [5,11] },
    ],
    science: [
      { code: "cam-prim-sc-1", outcome: "Biology: plants, animals, humans and the environment.",              ageBand: [5,11] },
      { code: "cam-prim-sc-2", outcome: "Physics: forces, motion, light, sound, Earth in space.",             ageBand: [6,11] },
      { code: "cam-prim-sc-3", outcome: "Chemistry: states of matter, materials, changes.",                   ageBand: [7,11] },
      { code: "cam-prim-sc-4", outcome: "Thinking & Working Scientifically: inquiry skills.",                 ageBand: [5,11] },
    ],
    global: [
      { code: "cam-prim-gp-1", outcome: "Global Perspectives: collaboration, communication, reflection.",     ageBand: [5,11] },
    ],
    ict: [
      { code: "cam-prim-ict-1", outcome: "Digital literacy: computational thinking and e-safety.",            ageBand: [5,11] },
    ],
    art: [
      { code: "cam-prim-ar-1", outcome: "Art & Design: exploration, experimentation and creation.",           ageBand: [5,11] },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  //   Cambridge Lower Secondary (11-14) — Stages 7-9
  // ═══════════════════════════════════════════════════════════════════
  "cam-lsec": {
    english: [
      { code: "cam-lsec-en-1", outcome: "Reading, writing and speaking at stage 7-9.",                       ageBand: [11,14] },
    ],
    maths: [
      { code: "cam-lsec-ma-1", outcome: "Algebra, geometry, probability and statistics at stage 7-9.",       ageBand: [11,14] },
    ],
    science: [
      { code: "cam-lsec-sc-1", outcome: "Biology, chemistry, physics — investigative practice.",             ageBand: [11,14] },
    ],
    global: [
      { code: "cam-lsec-gp-1", outcome: "Global Perspectives: research skills applied to global issues.",    ageBand: [11,14] },
    ],
    ict: [
      { code: "cam-lsec-ict-1", outcome: "Computing: programming, data, networks and impact of technology.", ageBand: [11,14] },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  //   UAE MOE (KG-Cycle 2, ages 4-14)
  // ═══════════════════════════════════════════════════════════════════
  "uae-moe": {
    arabic: [
      { code: "uae-moe-ar-1", outcome: "Arabic — reading, writing, speaking (KG–Cycle 1).",                 ageBand: [4,10] },
      { code: "uae-moe-ar-2", outcome: "Arabic — literature, grammar and composition (Cycle 2).",           ageBand: [10,14] },
    ],
    english: [
      { code: "uae-moe-en-1", outcome: "English — communication skills across four strands.",               ageBand: [5,14] },
    ],
    maths: [
      { code: "uae-moe-ma-1", outcome: "Numbers and operations aligned to MOE Cycle 1-2.",                  ageBand: [5,14] },
      { code: "uae-moe-ma-2", outcome: "Geometry, measurement and data analysis.",                          ageBand: [7,14] },
    ],
    science: [
      { code: "uae-moe-sc-1", outcome: "Life, physical, earth & space science aligned to MOE curriculum.",  ageBand: [5,14] },
    ],
    social: [
      { code: "uae-moe-ss-1", outcome: "Social Studies — UAE, GCC neighbours and world regions.",           ageBand: [5,12] },
      { code: "uae-moe-ss-2", outcome: "Moral, Social & Cultural Studies — Emirati identity, values.",      ageBand: [6,14] },
    ],
    islamic: [
      { code: "uae-moe-is-1", outcome: "Islamic Studies — Quran, Hadith, character.",                       ageBand: [5,14] },
    ],
    ict: [
      { code: "uae-moe-ict-1", outcome: "Design & Technology — digital innovation and coding basics.",      ageBand: [7,14] },
    ],
    art: [
      { code: "uae-moe-ar2-1", outcome: "Visual Arts — expression through drawing, painting and design.",   ageBand: [5,14] },
    ],
    pe: [
      { code: "uae-moe-pe-1", outcome: "Physical Education — movement, sport and health.",                  ageBand: [5,14] },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  //   Common Core (USA K-8)
  // ═══════════════════════════════════════════════════════════════════
  "common-core": {
    ela: [
      { code: "cc-ela-1", outcome: "Reading Literature — key ideas, craft, integration of knowledge.",       ageBand: [5,14] },
      { code: "cc-ela-2", outcome: "Reading Informational Text — key ideas, structure, evaluation.",         ageBand: [5,14] },
      { code: "cc-ela-3", outcome: "Writing — narrative, informative, argumentative texts.",                 ageBand: [6,14] },
      { code: "cc-ela-4", outcome: "Speaking & Listening — collaborate and present ideas.",                  ageBand: [5,14] },
      { code: "cc-ela-5", outcome: "Language — grammar, vocabulary acquisition.",                            ageBand: [5,14] },
    ],
    maths: [
      { code: "cc-ma-1", outcome: "Operations & Algebraic Thinking (K-5).",                                  ageBand: [5,11] },
      { code: "cc-ma-2", outcome: "Number & Operations in Base Ten and Fractions (K-5).",                   ageBand: [5,11] },
      { code: "cc-ma-3", outcome: "Ratios, Proportional Relationships & Expressions/Equations (6-8).",       ageBand: [11,14] },
      { code: "cc-ma-4", outcome: "Geometry, Measurement & Data (K-8).",                                     ageBand: [5,14] },
      { code: "cc-ma-5", outcome: "The Number System, Functions & Statistics (6-8).",                        ageBand: [11,14] },
    ],
    science: [
      { code: "cc-sc-1", outcome: "Life Science — molecules to ecosystems (NGSS).",                          ageBand: [5,14] },
      { code: "cc-sc-2", outcome: "Physical Science — matter, motion, energy (NGSS).",                       ageBand: [5,14] },
      { code: "cc-sc-3", outcome: "Earth & Space Science — weather, climate, universe (NGSS).",              ageBand: [5,14] },
      { code: "cc-sc-4", outcome: "Engineering Design — define, develop, optimise (NGSS).",                  ageBand: [6,14] },
    ],
    social: [
      { code: "cc-ss-1", outcome: "Geography — places, regions, use of maps and geographic tools.",         ageBand: [5,14] },
      { code: "cc-ss-2", outcome: "Human Systems — cultures, migration, economies, politics.",              ageBand: [8,14] },
      { code: "cc-ss-3", outcome: "History — chronology, cause & effect, historical thinking.",             ageBand: [7,14] },
      { code: "cc-ss-4", outcome: "Civics — foundations of American democracy and citizenship.",            ageBand: [8,14] },
      { code: "cc-ss-5", outcome: "Economics — scarcity, exchange, markets.",                                ageBand: [9,14] },
    ],
    arts: [
      { code: "cc-ar-1", outcome: "Visual Arts — creating, presenting, responding, connecting.",             ageBand: [5,14] },
      { code: "cc-ar-2", outcome: "Music — perform, create, respond, connect.",                              ageBand: [5,14] },
    ],
    tech: [
      { code: "cc-tech-1", outcome: "Digital Citizenship & Computational Thinking.",                         ageBand: [5,14] },
    ],
    pe: [
      { code: "cc-pe-1", outcome: "Health & Physical Education standards.",                                  ageBand: [5,14] },
    ],
  },
};

// ── Helpers ──────────────────────────────────────────────────────────

/** All outcomes for one curriculum. */
export function outcomesForCurriculum(curriculumId) {
  const bySubject = SYLLABUS[curriculumId] || {};
  return Object.entries(bySubject).flatMap(([subj, arr]) =>
    arr.map((o) => ({ ...o, subject: subj, curriculum: curriculumId })));
}

/** Filter outcomes by age within a curriculum. */
export function outcomesForAge(curriculumId, age) {
  return outcomesForCurriculum(curriculumId).filter((o) => {
    const [lo, hi] = o.ageBand || [0, 99];
    return age >= lo && age <= hi;
  });
}

/** Outcome lookup by code (used by the Term Report for evidence lines). */
const _CODE_MAP = (() => {
  const map = {};
  for (const [cur, subs] of Object.entries(SYLLABUS)) {
    for (const [subj, arr] of Object.entries(subs)) {
      for (const o of arr) map[o.code] = { ...o, subject: subj, curriculum: cur };
    }
  }
  return map;
})();

export function outcomeByCode(code) {
  return _CODE_MAP[code] || null;
}

/** Return the next outcome code in the SAME subject (and same
 *  curriculum when we can infer it) as the given code, or null if
 *  the given code is the last one. Feb 2026 · powers the "Master the
 *  next skill in {subject}" CTA on the OutcomeTest results screen so
 *  a child finishing an English test lands on the next English
 *  objective — never a Maths cameo. */
export function nextOutcomeInSubject(code) {
  const cur = _CODE_MAP[code];
  if (!cur) return null;
  const subj = cur.subject;
  const curriculum = cur.curriculum;
  // Prefer same curriculum first — most bookmarks / plans do that.
  const sameCurriculum = Object.values(_CODE_MAP)
    .filter((o) => o.subject === subj && o.curriculum === curriculum);
  const list = sameCurriculum.length > 1
    ? sameCurriculum
    : Object.values(_CODE_MAP).filter((o) => o.subject === subj);
  const idx = list.findIndex((o) => o.code === code);
  if (idx < 0 || idx >= list.length - 1) return null;
  return list[idx + 1];
}

/** All outcome codes across all curricula (used by MODULE_LIBRARY tags). */
export function allOutcomeCodes() {
  return Object.keys(_CODE_MAP);
}

/** Total outcome count — useful for coverage debugging. */
export function totalOutcomes() {
  return Object.keys(_CODE_MAP).length;
}
